import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'why', title: '1. Why handoff matters' },
  { id: 'how-it-works', title: '2. How a handoff works' },
  { id: 'recipients', title: '3. Routing & recipients' },
  { id: 'working-the-queue', title: '4. Working the queue' },
  { id: 'prompt', title: '5. Teaching the bot when to escalate' },
  { id: 'zendesk-zoho', title: '6. Helpdesk tickets' },
  { id: 'measuring', title: '7. What to watch' },
];

const HumanHandoffPage = () => {
  return (
    <>
      <NextSeo
        title="Human Handoff"
        description="Let your AI agent escalate to a person: how handoff requests are raised, routed by category, emailed to your team, and worked to resolution from the Handoff queue."
        canonical="https://chat.co/docs/human-handoff"
        openGraph={{
          url: 'https://chat.co/docs/human-handoff',
          title: 'Human Handoff | Chat.co',
          description:
            'How handoff requests are raised, routed by category, emailed to your team, and worked to resolution.',
          type: 'article',
          article: {
            publishedTime: '2026-08-30T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Operations',
            tags: ['handoff', 'escalation', 'live chat', 'support'],
          },
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Operations"
        categoryColor="text-blue-700"
        title="Human handoff"
        description="The exit door every honest AI agent needs. How escalation is raised, who it reaches, and how you work it to resolution."
        readingTime="8 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Website integration',
          href: PageLink.docsWebsiteIntegration,
        }}
        nextArticle={{
          title: 'Lead capture',
          href: PageLink.docsLeadCapture,
        }}
        keyTakeaways={[
          'A handoff carries the whole conversation, so your team never asks the visitor to start over',
          'Requests are categorised — GENERAL, TECHNICAL and SUPPORT by default — and routed per category',
          'Each category can have its own recipient list, falling back to a default, then to the bot owner',
          'The Handoff queue moves requests through Pending → In progress → Resolved or Cancelled',
          'A rising handoff rate on one topic is a knowledge gap, not a staffing problem',
        ]}>
        <p className="lead">
          The fastest way to destroy trust in an AI agent is to trap someone in it. A visitor who
          needs a person and cannot reach one does not conclude &ldquo;this bot has limits&rdquo; —
          they conclude the company is hiding. Handoff is the feature that prevents that, and it is
          worth setting up before you deploy rather than after the first complaint.
        </p>

        <section id="why" className="scroll-mt-28">
          <h2>1. Why handoff matters</h2>
          <p>
            Every knowledge agent has a boundary. Some questions need an account lookup, a judgement
            call, an exception, or simply a human being. What matters is not whether the boundary
            exists — it always does — but what happens when a visitor reaches it.
          </p>
          <p>
            A bot with handoff configured converts its own failure into a warm lead for your team. A
            bot without it converts the same moment into an abandoned session you never hear about.
          </p>

          <DocsCallout variant="tip" title="Configure it before launch, not after">
            Handoff is cheap to set up and expensive to retrofit — mostly because the conversations
            you lose in the meantime are invisible. If you are about to embed a bot on a customer-
            facing page, do this first.
          </DocsCallout>
        </section>

        <section id="how-it-works" className="scroll-mt-28">
          <h2>2. How a handoff works</h2>

          <DocsSteps>
            <DocsStep title="A visitor asks for a person">
              <p>
                Either because the bot offered — it should, when it cannot answer — or because the
                visitor asked directly.
              </p>
            </DocsStep>
            <DocsStep title="The request is created with full context">
              <p>
                Chat.co records the conversation it belongs to, a category, the visitor&apos;s name
                and email if they gave them, an optional message explaining what they need, and a
                snapshot of how long the conversation had run. Your team inherits the transcript.
              </p>
            </DocsStep>
            <DocsStep title="Your recipients are emailed">
              <p>
                The category decides who. The email carries the context, so someone can act on it
                from their inbox without opening the dashboard.
              </p>
            </DocsStep>
            <DocsStep title="Someone works it">
              <p>
                The request appears in the bot&apos;s <strong>Handoff</strong> queue as{' '}
                <strong>Pending</strong> and moves through the states below as your team picks it
                up.
              </p>
            </DocsStep>
          </DocsSteps>

          <DocsTable
            headers={['Status', 'Meaning']}
            rows={[
              [<strong key="p">Pending</strong>, 'Raised, emailed, nobody has picked it up yet'],
              [<strong key="i">In progress</strong>, 'Someone on your team has taken it'],
              [
                <strong key="r">Resolved</strong>,
                'Closed out, with a note recording what happened and who closed it',
              ],
              [
                <strong key="c">Cancelled</strong>,
                'Closed without being handled — duplicate, spam, or the visitor resolved it themselves',
              ],
            ]}
          />

          <p>
            Both closing states carry a note. Fill it in — six weeks later, the notes are the only
            record of what your bot could not do.
          </p>
        </section>

        <section id="recipients" className="scroll-mt-28">
          <h2>3. Routing &amp; recipients</h2>
          <p>
            Handoff requests carry a category. Out of the box these are <code>GENERAL</code>,{' '}
            <code>TECHNICAL</code> and <code>SUPPORT</code>, and you can define your own to match
            how your team is actually organised — <code>BILLING</code>, <code>RETURNS</code>,
            whatever you route on.
          </p>

          <h3>How recipients are resolved</h3>
          <p>Chat.co walks a three-step fallback, in this order:</p>

          <DocsSteps>
            <DocsStep title="Recipients configured for that specific category">
              <p>
                If <code>TECHNICAL</code> has its own recipient list, that list is used.
              </p>
            </DocsStep>
            <DocsStep title="The default handoff recipient list">
              <p>Used for any category without its own recipients.</p>
            </DocsStep>
            <DocsStep title="The bot owner's email address">
              <p>
                The last-resort fallback, so a handoff is never silently dropped. It also means a
                bot with no configuration at all still works — it just all lands on one person.
              </p>
            </DocsStep>
          </DocsSteps>

          <DocsCallout variant="warning" title="Set a default list, even a small one">
            Relying on the owner fallback means every escalation goes to one inbox — and stops
            entirely when that person is on holiday. A default list of two or three people costs a
            minute to set up and removes a single point of failure.
          </DocsCallout>
        </section>

        <section id="working-the-queue" className="scroll-mt-28">
          <h2>4. Working the queue</h2>
          <p>
            Open <strong>Handoff</strong> under Interactions in the bot sidebar. Each request opens
            onto the conversation that produced it, so the first thing your team reads is what the
            visitor actually said — not a summary of it.
          </p>

          <p>Two habits make the difference between a queue that works and one that rots:</p>

          <DocsCardGrid columns={2}>
            <DocsCard icon="tabler:progress" title="Move it to In progress when you take it">
              Otherwise two people work the same request and a third assumes someone has it. The
              status is the only signal your team has.
            </DocsCard>
            <DocsCard icon="tabler:notes" title="Write a real note when you close it">
              &ldquo;Resolved&rdquo; with no note tells you nothing later. One sentence about what
              the visitor needed turns the queue into a source of product truth.
            </DocsCard>
          </DocsCardGrid>

          <p>
            Team members can be given access to Handoff without access to everything else —{' '}
            <Link href={PageLink.docsTeamRoles}>Team, roles &amp; seats</Link> covers the permission
            model.
          </p>
        </section>

        <section id="prompt" className="scroll-mt-28">
          <h2>5. Teaching the bot when to escalate</h2>
          <p>
            A model will not offer a human unless you tell it to. This belongs in your system prompt
            under <strong>Tune Bot → Behavior</strong>, and it should be specific about triggers
            rather than vague about willingness.
          </p>

          <p>Worth naming explicitly:</p>
          <ul>
            <li>
              <strong>Anything the knowledge base does not cover.</strong> Not an apology and a
              guess — an offer.
            </li>
            <li>
              <strong>Anything account-specific.</strong> Order status, billing, personal data. The
              bot cannot see it and should say so.
            </li>
            <li>
              <strong>Any sign of frustration.</strong> Repetition, escalating language, a second
              rephrasing of the same question.
            </li>
            <li>
              <strong>Any direct request for a person.</strong> This should never require
              negotiation. A bot that argues about it is worse than no bot.
            </li>
          </ul>

          <DocsCallout variant="tip" title="Offer, don't just permit">
            &ldquo;I can connect you with someone from our team — would that help?&rdquo; converts
            far better than waiting for the visitor to think of asking. Most people do not know
            handoff is an option unless the bot says so.
          </DocsCallout>
        </section>

        <section id="zendesk-zoho" className="scroll-mt-28">
          <h2>6. Helpdesk tickets</h2>
          <p>
            If your team lives in a helpdesk rather than an inbox, connect one. Zoho Desk in
            particular can receive tickets created from handoff requests, so the escalation lands
            where your agents already work and inherits your existing SLA and assignment rules.
          </p>
          <p>
            Both Zendesk and Zoho Desk are configured from the bot&apos;s{' '}
            <strong>Integrations</strong> page — see{' '}
            <Link href={PageLink.docsChannelIntegrations}>channel integrations</Link>.
          </p>
        </section>

        <section id="measuring" className="scroll-mt-28">
          <h2>7. What to watch</h2>

          <DocsTable
            headers={['Signal', 'What it usually means']}
            rows={[
              [
                <strong key="a">Handoff rate climbing</strong>,
                'Either the bot got worse or your traffic changed. Read the transcripts before assuming either.',
              ],
              [
                <strong key="b">Handoffs clustering on one topic</strong>,
                'A knowledge gap with a name. This is the single most actionable signal the queue produces.',
              ],
              [
                <strong key="c">Handoff rate near zero</strong>,
                'Rarely good news. Usually the bot never offers, and visitors are leaving instead of escalating.',
              ],
              [
                <strong key="d">Long time-to-first-reply</strong>,
                'Recipients are wrong, or the notifications are landing in a mailbox nobody reads.',
              ],
            ]}
          />

          <p>
            The second row is where the value is. A cluster of handoffs about shipping times is not
            a support problem — it is a missing page in your knowledge base.{' '}
            <Link href={PageLink.docsAnalyticsRoi}>Knowledge Gaps</Link> attacks the same problem
            from the other direction, and the two together close the loop: the bot fails, a human
            covers it, and the failure becomes content so it does not happen again.
          </p>

          <DocsCardGrid columns={2}>
            <DocsCard
              icon="tabler:bulb"
              title="Analytics, ROI &amp; knowledge gaps"
              href={PageLink.docsAnalyticsRoi}>
              Turn unanswered questions into knowledge base content.
            </DocsCard>
            <DocsCard icon="tabler:user-plus" title="Lead capture" href={PageLink.docsLeadCapture}>
              The other way a conversation becomes a person your team follows up with.
            </DocsCard>
          </DocsCardGrid>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default HumanHandoffPage;
